import { useEffect, useState, useRef } from 'react';

/**
 * Custom hook to track the section which is currently active based on the scroll position.
 * @param {Array} sections
 * @param {object} options
 * @returns {string} - The ID of the currently active section
 */
export const useActiveSection = (sections = [], options = {}) => {
  const [activeSection, setActiveSection] = useState('');
  const sectionsRef = useRef(sections);
  const SCROLL_HEIGHT = 350;

  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  useEffect(() => {
    if (!sections.length) {
      return undefined;
    }

    const validSections = sections.filter((section) => section && section.length > 0);

    if (!validSections.length) {
      return undefined;
    }

    /**
     * Handles updating the active section
     */
    const updateActiveSection = () => {
      const scrollTop = window.scrollY;

      if (validSections.length > 0 && validSections[0]) {
        const firstSectionElement = document.querySelector(sectionsRef.current[0]);

        if (firstSectionElement) {
          const firstSectionTop = firstSectionElement.getBoundingClientRect().top + window.scrollY;

          if (scrollTop < firstSectionTop - SCROLL_HEIGHT) {
            setActiveSection('');
            return;
          }
        }
      }

      const passedSections = [];
      validSections.forEach((sectionId) => {
        const element = document.querySelector(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          const absoluteTop = rect.top + window.scrollY;

          if (absoluteTop <= scrollTop + SCROLL_HEIGHT) {
            passedSections.push({ id: sectionId, position: absoluteTop });
          }
        }
      });

      if (passedSections.length > 0) {
        passedSections.sort((a, b) => a.position - b.position);
        const mostRecentSection = passedSections[passedSections.length - 1];
        setActiveSection(mostRecentSection.id);
      } else {
        setActiveSection('');
      }
    };

    const observerOptions = {
      rootMargin: '-350px 0px -60% 0px',
      threshold: 0,
      ...options,
    };

    const observer = new IntersectionObserver(() => {
      updateActiveSection();
    }, observerOptions);

    validSections.forEach((section) => {
      if (!section) {
        return;
      }

      const element = document.querySelector(section);
      if (element) {
        observer.observe(element);
      }
    });

    /**
     * Handles the scroll event
     */
    const handleScroll = () => {
      updateActiveSection();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    updateActiveSection();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      validSections.forEach((section) => {
        if (!section) {
          return;
        }

        const element = document.querySelector(section);
        if (element) {
          observer.unobserve(element);
        }
      });
      observer.disconnect();
    };
  }, [sections, options]);

  return activeSection;
};
